import { getSlateColorToken } from "@faire/retailer-shared/SlateMappings/getSlateColorToken";
import { Column } from "@faire/web--source/slate/Layout";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { ISectionStyle } from "@faire/web-api--source/indigofair/data/ISectionStyle";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
// eslint-disable-next-line no-restricted-imports
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import React from "react";

import { getResponsiveStyles } from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import { getContentWidthPixels } from "@faire/retailer/components/SDUI/__internal__/Styling/sectionContentWidth";
import {
  getConsolidatedSpacingValues,
  getResponsiveSpacingPixels,
} from "@faire/retailer/components/SDUI/__internal__/Styling/spacing";

import * as styles from "./styles.css";

export interface ISDUISectionStyleWrapperProps {
  sectionStyles: ISectionStyle | undefined;
  /**
   * Whether the section should be visible on a specific viewport.
   * By default, the section will be visible on all viewports.
   */
  sectionVisibility?: ResponsiveValues<boolean>;
}

export const SDUISectionStyleWrapper: React.FC<
  React.PropsWithChildren<ISDUISectionStyleWrapperProps>
> = ({ sectionStyles, sectionVisibility, children }) => {
  const clientTopPadding = getResponsiveSpacingPixels(
    Spacing.SPACING_NONE,
    sectionStyles?.responsive_top_padding
  );
  const clientHorizontalPadding = getResponsiveSpacingPixels(
    Spacing.SPACING_NONE,
    sectionStyles?.responsive_horizontal_padding
  );
  const clientBottomPadding = getResponsiveSpacingPixels(
    Spacing.SPACING_NONE,
    sectionStyles?.responsive_bottom_padding
  );
  const responsivePaddingShorthand = getConsolidatedSpacingValues(
    clientTopPadding,
    clientBottomPadding,
    clientHorizontalPadding,
    clientHorizontalPadding
  );
  const { className: paddingClassnames, style: paddingStyles } =
    getResponsiveStyles(responsivePaddingShorthand, {
      mobileAndAbove: [styles.mobileAndAbovePadding, styles.varMobilePadding],
      tabletAndAbove: [styles.tabletAndAbovePadding, styles.varTabletPadding],
      desktopAndAbove: [
        styles.desktopAndAbovePadding,
        styles.varDesktopPadding,
      ],
    });

  return (
    <Column
      align="center"
      justify="center"
      className={classNames(styles.sectionWrapper)}
      style={assignInlineVars({
        [styles.varBackgroundColor]: getSlateColorToken(
          sectionStyles?.background_color,
          ColorTokenKey.COLOR_TOKEN_KEY_UNKNOWN
        ),
      })}
    >
      <Column
        data-test-id="section-style-content-wrapper"
        align="center"
        justify="center"
        className={classNames(
          styles.sectionContentWrapper,
          paddingClassnames,
          ...getSectionVisibilityClassNames(sectionVisibility)
        )}
        style={assignInlineVars({
          [styles.varContentMaxWidth]: getContentWidthPixels(
            sectionStyles?.content_width
          ),
          ...paddingStyles,
        })}
      >
        {children}
      </Column>
    </Column>
  );
};

const getSectionVisibilityClassNames = (
  sectionVisibility: ResponsiveValues<boolean> | undefined
) => {
  if (!sectionVisibility) {
    return [];
  }

  return Object.entries(sectionVisibility).map(([viewport, shouldShow]) => {
    return shouldShow
      ? VISIBLE_CLASS_NAMES[viewport as keyof ResponsiveValues<string>]
      : HIDDEN_CLASS_NAMES[viewport as keyof ResponsiveValues<string>];
  });
};

const VISIBLE_CLASS_NAMES: ResponsiveValues<string> = {
  mobileAndAbove: styles.mobileAndAboveDisplayFlex,
  tabletAndAbove: styles.tabletAndAboveDisplayFlex,
  desktopAndAbove: styles.desktopAndAboveDisplayFlex,
};

const HIDDEN_CLASS_NAMES: ResponsiveValues<string> = {
  mobileAndAbove: styles.mobileAndAboveDisplayNone,
  tabletAndAbove: styles.tabletAndAboveDisplayNone,
  desktopAndAbove: styles.desktopAndAboveDisplayNone,
};
